import type {
  SpeechService,
  SpeechSession,
  TranscribeOptions,
  TranscriptChunk,
} from '../../ports/speech'
import { BackendError } from '../../errors'
import { createTransport, type Transport } from '../http/transport'
import { normalizeBaseUrl, type LiteLlmConnection } from './config'

export interface LiteLlmSpeechConfig extends LiteLlmConnection {
  /**
   * Transcription model as listed by `/v1/models` (e.g. "whisper-1").
   * Empty means dictation is not set up — `start()` then fails with a notice.
   */
  model: string
}

/**
 * Container formats in preference order. Chromium (Word on Windows) records
 * WebM/Opus, WKWebView (Word on macOS) only MP4/AAC — both are accepted by the
 * transcription endpoint, and the file extension has to match what we recorded.
 */
const CONTAINERS = [
  { mimeType: 'audio/webm;codecs=opus', ext: 'webm' },
  { mimeType: 'audio/webm', ext: 'webm' },
  { mimeType: 'audio/mp4', ext: 'mp4' },
  { mimeType: 'audio/ogg;codecs=opus', ext: 'ogg' },
]

function pickContainer(): { mimeType?: string; ext: string } {
  if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
    for (const c of CONTAINERS) {
      if (MediaRecorder.isTypeSupported(c.mimeType)) return c
    }
  }
  return { ext: 'webm' }
}

/**
 * Dictation via LiteLLM's OpenAI-compatible `/v1/audio/transcriptions`.
 *
 * That endpoint transcribes a finished recording, so there are no interim
 * results: the session records until `stop()`, uploads once and yields a single
 * final chunk. The composer shows a "transcribing" state for that gap.
 */
export class LiteLlmSpeechService implements SpeechService {
  private readonly t: Transport

  constructor(private readonly config: LiteLlmSpeechConfig) {
    this.t = createTransport({
      baseUrl: normalizeBaseUrl(config.baseUrl),
      getAccessToken: async () => config.virtualKey,
      fetch: config.fetch,
      // Upload plus transcription of a long dictation needs more room than a chat turn.
      timeoutMs: config.timeoutMs ?? 120_000,
      onError: config.onError,
    })
  }

  async start(opts?: TranscribeOptions): Promise<SpeechSession> {
    if (!this.config.model.trim()) {
      throw new BackendError({
        code: 'bad_request',
        message:
          'Kein Transkriptions-Modell ausgewählt. Bitte in den Einstellungen ein Modell für die Spracheingabe festlegen.',
      })
    }

    const stream = await captureMicrophone()
    const { mimeType, ext } = pickContainer()
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    const parts: Blob[] = []

    let resolveStopped: () => void = () => {}
    const stopped = new Promise<void>((resolve) => {
      resolveStopped = resolve
    })
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) parts.push(e.data)
    }
    recorder.onstop = () => resolveStopped()
    recorder.onerror = () => resolveStopped()
    recorder.start()

    const finish = async (): Promise<void> => {
      if (recorder.state === 'inactive') resolveStopped()
      else recorder.stop()
      await stopped
    }

    // Cancelling the surrounding request also ends the recording.
    opts?.signal?.addEventListener('abort', () => void finish(), { once: true })

    const transcribe = (blob: Blob) => this.upload(blob, ext, opts)

    async function* transcripts(): AsyncIterable<TranscriptChunk> {
      try {
        await stopped
        if (opts?.signal?.aborted) return
        const blob = new Blob(parts, { type: mimeType ?? 'audio/webm' })
        // Nothing recorded (mic muted, stopped instantly) — no request, no error.
        const text = blob.size > 0 ? await transcribe(blob) : ''
        yield { text, isFinal: true }
      } finally {
        for (const track of stream.getTracks()) track.stop()
      }
    }

    return { transcripts: transcripts(), stop: finish }
  }

  private async upload(blob: Blob, ext: string, opts?: TranscribeOptions): Promise<string> {
    const form = new FormData()
    form.append('file', blob, `dictation.${ext}`)
    form.append('model', this.config.model)
    // Without a language hint the model detects it; the composer's locale wins
    // when the caller passes one.
    if (opts?.locale) form.append('language', opts.locale)
    form.append('response_format', 'json')

    const res = await this.t.json<{ text?: string }>('POST', 'v1/audio/transcriptions', {
      body: form,
      signal: opts?.signal,
    })
    return res.text?.trim() ?? ''
  }
}

/** Ask for the microphone, with errors the user can act on. */
async function captureMicrophone(): Promise<MediaStream> {
  const devices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
  if (!devices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    throw new BackendError({
      code: 'unknown',
      message: 'Spracheingabe wird in dieser Umgebung nicht unterstützt (kein Mikrofon-Zugriff).',
    })
  }
  try {
    return await devices.getUserMedia({ audio: true })
  } catch (e) {
    const name = (e as { name?: string }).name
    const message =
      name === 'NotAllowedError' || name === 'SecurityError'
        ? 'Kein Zugriff auf das Mikrofon. Bitte den Zugriff für Word in den Systemeinstellungen erlauben und es erneut versuchen.'
        : name === 'NotFoundError'
          ? 'Kein Mikrofon gefunden.'
          : 'Das Mikrofon konnte nicht gestartet werden.'
    throw new BackendError({ code: 'forbidden', message, cause: e })
  }
}

/** SpeechService backed by a LiteLLM transcription model. */
export function createLiteLlmSpeech(config: LiteLlmSpeechConfig): SpeechService {
  return new LiteLlmSpeechService(config)
}
